class VideoSliderPopup {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId || section.dataset.blockId;
    this.slider = section.querySelector('.video-slider');
    this.slides = section.querySelectorAll('.video-slide');
    this.prevBtn = section.querySelector('.video-slider-arrow-prev');
    this.nextBtn = section.querySelector('.video-slider-arrow-next');
    this.dotsContainer = section.querySelector('.video-slider-dots');
    this.modal = document.getElementById(`video-modal-${this.sectionId}`);
    
    if (!this.slider || this.slides.length === 0) return;
    
    this.modalPlayer = this.modal ? this.modal.querySelector('.video-modal-player') : null;
    this.modalTitle = this.modal ? this.modal.querySelector('.video-modal-title') : null;
    this.closeBtn = this.modal ? this.modal.querySelector('.video-modal-close') : null;
    this.modalOverlay = this.modal ? this.modal.querySelector('.video-modal-overlay') : null;
    
    this.slidesPerView = this.getSlidesPerView();
    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.scrollLeft = 0;
    this.currentPlayer = null;
    
    this.init();
  }

  init() {
    this.setupSlider();
    this.setupEventListeners();
    this.setupIntersectionObserver();
    if (this.dotsContainer) {
      this.createDots();
    }
  }

  getSlidesPerView() {
    const desktop = parseInt(this.slider.dataset.slidesDesktop) || 4;
    const mobile = parseInt(this.slider.dataset.slidesMobile) || 1;
    return window.innerWidth <= 768 ? mobile : desktop;
  }

  setupSlider() {
    this.updateSliderPosition();
    this.updateArrows();
    this.updateDots();
  }

  createDots() {
    this.dotsContainer.innerHTML = '';
    const totalPages = Math.ceil(this.slides.length / this.slidesPerView);
    
    for (let i = 0; i < totalPages; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'video-slider-dot';
      if (i === 0) dot.classList.add('active');
      dot.setAttribute('aria-label', `Go to page ${i + 1}`);
      dot.addEventListener('click', () => this.goToPage(i));
      this.dotsContainer.appendChild(dot);
    }
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateSliderPosition();
      this.updateArrows();
      this.updateDots();
    }
  }

  nextSlide() {
    const maxIndex = Math.max(0, this.slides.length - this.slidesPerView);
    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
      this.updateSliderPosition();
      this.updateArrows();
      this.updateDots();
    }
  }

  goToPage(page) {
    this.currentIndex = page * this.slidesPerView;
    if (this.currentIndex > this.slides.length - this.slidesPerView) {
      this.currentIndex = Math.max(0, this.slides.length - this.slidesPerView);
    }
    this.updateSliderPosition();
    this.updateArrows();
    this.updateDots();
  }

  updateSliderPosition() {
    // No transform needed - all videos are visible side by side
    // Scroll to position if needed
    if (this.slides.length === 0) return;
    
    const slideWidth = this.slides[0].offsetWidth;
    const gap = 12;
    const scrollPosition = this.currentIndex * (slideWidth + gap);
    const wrapper = this.slider.closest('.video-slider-wrapper');
    if (wrapper) {
      wrapper.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  }

  updateArrows() {
    if (!this.prevBtn || !this.nextBtn) return;
    
    const maxIndex = Math.max(0, this.slides.length - this.slidesPerView);
    
    if (this.currentIndex <= 0) {
      this.prevBtn.classList.add('disabled');
    } else {
      this.prevBtn.classList.remove('disabled');
    }
    
    if (this.currentIndex >= maxIndex) {
      this.nextBtn.classList.add('disabled');
    } else {
      this.nextBtn.classList.remove('disabled');
    }
  }

  updateDots() {
    if (!this.dotsContainer) return;
    
    const dots = this.dotsContainer.querySelectorAll('.video-slider-dot');
    const currentPage = Math.floor(this.currentIndex / this.slidesPerView);
    
    dots.forEach((dot, index) => {
      if (index === currentPage) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
  }

  setupEventListeners() {
    // Arrow navigation
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }

    // Thumbnail click events
    this.slides.forEach((slide) => {
      const btn = slide.querySelector('.video-thumbnail-btn');
      if (btn) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openModal(btn);
        });
      }
    });

    // Modal close events
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeModal());
    }
    if (this.modalOverlay) {
      this.modalOverlay.addEventListener('click', () => this.closeModal());
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (this.modal && this.modal.getAttribute('aria-hidden') === 'false') {
        if (e.key === 'Escape') this.closeModal();
      }
    });

    // Touch and drag events
    this.setupTouchEvents();

    // Window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.slidesPerView = this.getSlidesPerView();
        this.updateSliderPosition();
        this.updateArrows();
        this.updateDots();
      }, 250);
    });
  }

  setupTouchEvents() {
    this.slider.addEventListener('touchstart', (e) => this.touchStart(e));
    this.slider.addEventListener('touchmove', (e) => this.touchMove(e));
    this.slider.addEventListener('touchend', () => this.touchEnd());
    
    this.slider.addEventListener('mousedown', (e) => this.dragStart(e));
    this.slider.addEventListener('mousemove', (e) => this.dragMove(e));
    this.slider.addEventListener('mouseup', () => this.dragEnd());
    this.slider.addEventListener('mouseleave', () => this.dragEnd());
  }

  touchStart(e) {
    this.isDragging = true;
    this.startX = e.touches[0].pageX - this.slider.offsetLeft;
    this.scrollLeft = this.slider.scrollLeft;
    this.slider.style.cursor = 'grabbing';
  }

  touchMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    const x = e.touches[0].pageX - this.slider.offsetLeft;
    const walk = (x - this.startX) * 2;
    this.slider.scrollLeft = this.scrollLeft - walk;
  }

  touchEnd() {
    this.isDragging = false;
    this.slider.style.cursor = 'grab';
  }

  dragStart(e) {
    if (e.button !== 0) return; // Only left mouse button
    this.isDragging = true;
    this.startX = e.pageX - this.slider.offsetLeft;
    this.scrollLeft = this.slider.scrollLeft;
    this.slider.style.cursor = 'grabbing';
  }

  dragMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    const x = e.pageX - this.slider.offsetLeft;
    const walk = (x - this.startX) * 2;
    this.slider.scrollLeft = this.scrollLeft - walk;
  }

  dragEnd() {
    this.isDragging = false;
    this.slider.style.cursor = 'grab';
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;
    
    const autoplay = this.slider.dataset.autoplay === 'true';
    if (!autoplay) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target.querySelector('.video-thumbnail-player');
        if (video) {
          if (entry.isIntersecting) {
            video.play().catch(() => {
              // Autoplay failed, ignore
            });
          } else {
            video.pause();
          }
        }
      });
    }, {
      threshold: 0.5
    });

    this.slides.forEach((slide) => {
      observer.observe(slide);
    });
  }

  openModal(btn) {
    const videoUrl = btn.getAttribute('data-video-url') || btn.dataset.videoUrl;
    const videoTitle = btn.getAttribute('data-video-title') || btn.dataset.videoTitle;
    const videoType = btn.getAttribute('data-video-type') || btn.dataset.videoType || 'url';
    
    if (!videoUrl || videoUrl.trim() === '') {
      console.error('No video URL found');
      return;
    }

    if (!this.modal || !this.modalPlayer) {
      console.error('Modal not found');
      return;
    }

    // Set title
    if (videoTitle && this.modalTitle) {
      this.modalTitle.textContent = videoTitle;
      this.modalTitle.style.display = 'block';
    } else if (this.modalTitle) {
      this.modalTitle.style.display = 'none';
    }

    // Load video
    this.loadVideo(videoUrl, videoType);

    // Show modal
    this.modal.setAttribute('aria-hidden', 'false');
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    if (!this.modal) return;
    
    this.modal.setAttribute('aria-hidden', 'true');
    this.modal.classList.remove('active');
    document.body.style.overflow = '';

    // Stop video
    this.stopVideo();
  }

  loadVideo(url, type) {
    if (!this.modalPlayer) return;
    
    this.modalPlayer.innerHTML = '';

    // Ensure URL is absolute
    let videoUrl = url.trim();
    if (videoUrl && !videoUrl.startsWith('http://') && !videoUrl.startsWith('https://') && !videoUrl.startsWith('//')) {
      if (videoUrl.startsWith('/')) {
        videoUrl = window.location.origin + videoUrl;
      } else {
        videoUrl = 'https://' + videoUrl;
      }
    }

    if (type === 'youtube') {
      const videoId = this.getYouTubeVideoId(videoUrl);
      if (videoId) {
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';
        this.modalPlayer.appendChild(iframe);
        this.currentPlayer = { type: 'youtube', element: iframe };
        return;
      }
    }

    if (type === 'vimeo') {
      const videoId = this.getVimeoVideoId(videoUrl);
      if (videoId) {
        const iframe = document.createElement('iframe');
        iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';
        this.modalPlayer.appendChild(iframe);
        this.currentPlayer = { type: 'vimeo', element: iframe };
        return;
      }
    }

    // HTML5 video
    const video = document.createElement('video');
    video.src = videoUrl;
    video.controls = true;
    video.autoplay = true;
    video.playsInline = true;
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'contain';
    
    // Remove custom error overlay - let browser handle errors naturally
    video.addEventListener('error', (e) => {
      console.error('Video loading error:', e, 'URL:', videoUrl);
      // Just log the error, don't show custom overlay
    });
    
    this.modalPlayer.appendChild(video);
    this.currentPlayer = { type: 'html5', element: video };
  }

  stopVideo() {
    if (this.currentPlayer && this.currentPlayer.element) {
      if (this.currentPlayer.type === 'html5') {
        this.currentPlayer.element.pause();
        this.currentPlayer.element.currentTime = 0;
      } else if (this.currentPlayer.type === 'youtube' || this.currentPlayer.type === 'vimeo') {
        this.currentPlayer.element.src = '';
      }
    }
    this.currentPlayer = null;
    if (this.modalPlayer) {
      this.modalPlayer.innerHTML = '';
    }
  }

  getYouTubeVideoId(url) {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  }

  getVimeoVideoId(url) {
    const regExp = /(?:vimeo\.com\/)(?:.*\/)?(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  }
}

// Initialize all video slider popup sections and blocks
function initVideoSliderPopups() {
  // Initialize sections
  const sections = document.querySelectorAll('.video-slider-popup-section');
  sections.forEach(section => {
    if (!section.dataset.initialized) {
      new VideoSliderPopup(section);
      section.dataset.initialized = 'true';
    }
  });

  // Initialize blocks (snippets)
  const blocks = document.querySelectorAll('.video-slider-popup-block');
  blocks.forEach(block => {
    if (!block.dataset.initialized) {
      new VideoSliderPopup(block);
      block.dataset.initialized = 'true';
    }
  });
}

// Run initialization
function runInit() {
  setTimeout(() => {
    initVideoSliderPopups();
  }, 100);
}

// DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runInit);
} else {
  runInit();
}

// Window load
window.addEventListener('load', runInit);

// Shopify section events
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.video-slider-popup-section');
    if (section) {
      section.dataset.initialized = 'false';
      new VideoSliderPopup(section);
      section.dataset.initialized = 'true';
    }
    const block = event.target.querySelector('.video-slider-popup-block');
    if (block) {
      block.dataset.initialized = 'false';
      new VideoSliderPopup(block);
      block.dataset.initialized = 'true';
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('.video-slider-popup-section');
    if (section && !section.dataset.initialized) {
      new VideoSliderPopup(section);
      section.dataset.initialized = 'true';
    }
    const block = event.target.closest('.video-slider-popup-block');
    if (block && !block.dataset.initialized) {
      new VideoSliderPopup(block);
      block.dataset.initialized = 'true';
    }
  });
}
